// Client-side model for the `resolve_plan_selection` bootstrap signal. The
// service reads the member's plans, persists the resolved selection server-side,
// and returns a discriminated `status` the client routes on. Kept separate from
// the onboarding machine so the status→resolution mapping stays a pure,
// unit-testable function independent of the socket call.

// Response body of `resolve_plan_selection`. `status` is discriminated;
// `selected_plan_id` is present only when `status === 'resolved'`. Typed at the
// call site — the socket layer does not type responses per message.
export type ResolvePlanSelectionResponse = {
  status?: string;
  selected_plan_id?: string;
};

// The routing decision the onboarding machine branches on.
//   - resolved      → single plan, resolved server-side → skip the gate, go Home (no flash)
//   - needsSelection → multiple plans → show the picker gate
//   - noPlans        → no coverage → no-coverage screen
//   - error          → server failed closed → retryable error screen
//   - unavailable    → service unreachable / not deployed → fall back to the gate (additive)
//   - notApplicable  → plan switcher not a feature here (capability off, or server
//                      `not_applicable` when no plans extension) → go Home, no gate
export type PlanSelectionResolution =
  | 'resolved'
  | 'needsSelection'
  | 'noPlans'
  | 'error'
  | 'unavailable'
  | 'notApplicable';

// Maps a `resolve_plan_selection` response to a routing decision.
//   - `resolved` without a usable id is malformed and fails closed to `error`
//     rather than routing Home with no selection.
//   - an unknown/absent status degrades to `unavailable` (fall back to the gate)
//     so a contract mismatch never hard-errors.
export const mapPlanSelectionResolution = (
  response: ResolvePlanSelectionResponse | undefined,
): PlanSelectionResolution => {
  switch (response?.status) {
    case 'resolved':
      return response.selected_plan_id?.trim() ? 'resolved' : 'error';
    case 'needs_selection':
      return 'needsSelection';
    case 'no_plans':
      return 'noPlans';
    case 'error':
      return 'error';
    case 'not_applicable':
      return 'notApplicable';
    default:
      return 'unavailable';
  }
};
